import React, { useMemo, useState } from "react";

function getDeckMetadata(note) {
  if (typeof note !== "string" || !note.includes("study-guide-slides")) return null;

  if (typeof DOMParser === "undefined") return null;
  const document = new DOMParser().parseFromString(note, "text/html");
  const deck = document.querySelector(".study-guide-slides");
  if (!deck) return null;

  const count = Number.parseInt(deck.getAttribute("data-slide-count") || "", 10);
  const sourceTemplate = deck.getAttribute("data-slide-src-template") || "";
  const pageNumberPadding = Number.parseInt(deck.getAttribute("data-slide-page-padding") || "3", 10);
  const encodedTitles = deck.getAttribute("data-slide-titles") || "";
  const knowledgeCheckSlides = (deck.getAttribute("data-knowledge-check-slides") || "")
    .split(",")
    .map((value) => Number.parseInt(value, 10))
    .filter((page) => Number.isInteger(page) && page >= 1 && page <= count);
  if (!Number.isInteger(count) || count < 1 || !Number.isInteger(pageNumberPadding) || pageNumberPadding < 1 || !sourceTemplate.includes("{{page}}")) return null;

  try {
    const titles = JSON.parse(decodeURIComponent(encodedTitles));
    if (!Array.isArray(titles) || titles.length !== count) return null;
    return { count, sourceTemplate, pageNumberPadding, titles, knowledgeCheckSlides };
  } catch {
    return null;
  }
}

export default function StudyGuideSlideDeck({ note, title }) {
  const deck = useMemo(() => getDeckMetadata(note), [note]);
  const [slideIndex, setSlideIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [knowledgeChecksOpen, setKnowledgeChecksOpen] = useState(false);

  if (!deck) return null;

  const page = slideIndex + 1;
  const imageSource = deck.sourceTemplate.replace("{{page}}", String(page).padStart(deck.pageNumberPadding, "0"));
  const previous = () => setSlideIndex((current) => Math.max(0, current - 1));
  const next = () => setSlideIndex((current) => Math.min(deck.count - 1, current + 1));
  const matches = searchTerm.trim()
    ? deck.titles
      .map((slideTitle, index) => ({ slideTitle, index }))
      .filter(({ slideTitle }) => slideTitle.toLowerCase().includes(searchTerm.trim().toLowerCase()))
      .slice(0, 8)
    : [];
  const previousKnowledgeCheck = [...deck.knowledgeCheckSlides].reverse().find((pageNumber) => pageNumber < page);
  const nextKnowledgeCheck = deck.knowledgeCheckSlides.find((pageNumber) => pageNumber > page);
  const currentKnowledgeCheckIndex = deck.knowledgeCheckSlides.indexOf(page);

  return (
    <section className="study-guide-slide-deck" aria-label={`${title || "Study guide"} slides`}>
      <div className="study-guide-slide-toggle">
        <div>
          <strong>{title || "Study guide slides"}</strong>
          <span>{deck.count} slides · current slide stays visible</span>
        </div>
        <button
          type="button"
          className="action-button"
          aria-expanded={isOpen}
          onClick={() => {
            if (!isOpen) {
              setSearchTerm("");
            }
            setIsOpen((current) => !current);
          }}
        >
          {isOpen ? "Hide titles" : "Click to view titles"}
        </button>
      </div>
      <div className="study-guide-slide-toolbar">
        <span aria-live="polite">Slide {page} of {deck.count}</span>
        <div>
          <button type="button" className="action-button action-button-secondary" onClick={() => setSlideIndex(0)} disabled={slideIndex === 0}>
            First slide
          </button>
          <button type="button" className="action-button action-button-secondary" onClick={previous} disabled={slideIndex === 0}>
            Back
          </button>
          <button type="button" className="action-button" onClick={next} disabled={slideIndex === deck.count - 1}>
            Next
          </button>
        </div>
      </div>
      {isOpen ? (
        <div className="study-guide-slide-search">
          <label htmlFor="study-guide-slide-search-input">Search slide titles</label>
          <input
            id="study-guide-slide-search-input"
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="For example: ADOMs"
            autoComplete="off"
          />
          {searchTerm.trim() ? (
            <div className="study-guide-slide-search-results" role="listbox" aria-label="Matching slide titles">
              {matches.length ? matches.map(({ slideTitle, index }) => (
                <button
                  type="button"
                  key={`${index}-${slideTitle}`}
                  role="option"
                  onClick={() => {
                    setSlideIndex(index);
                    setSearchTerm("");
                  }}
                >
                  <span>Slide {index + 1}</span>
                  {slideTitle}
                </button>
              )) : <p>No matching slide titles.</p>}
            </div>
          ) : null}
          <label htmlFor="study-guide-slide-title-list">Click a slide title to open it</label>
          <select
            id="study-guide-slide-title-list"
            className="study-guide-slide-title-list"
            size="8"
            defaultValue=""
            onChange={(event) => {
              const index = Number.parseInt(event.target.value, 10);
              if (Number.isInteger(index) && index >= 0 && index < deck.count) {
                setSlideIndex(index);
                setSearchTerm("");
              }
              event.target.value = "";
            }}
          >
            <option value="" disabled>Select a slide title</option>
            {deck.titles.map((slideTitle, index) => (
              <option key={`${index}-${slideTitle}`} value={index}>
                Slide {index + 1}: {slideTitle}
              </option>
            ))}
          </select>
          {deck.knowledgeCheckSlides.length ? <div className="knowledge-check-controls">
            <div className="knowledge-check-header">
              <strong>Knowledge Check slides ({deck.knowledgeCheckSlides.length})</strong>
              <button
                type="button"
                className="action-button action-button-secondary"
                aria-expanded={knowledgeChecksOpen}
                onClick={() => setKnowledgeChecksOpen((current) => !current)}
              >
                {knowledgeChecksOpen ? "Hide Knowledge Checks" : "View Knowledge Checks"}
              </button>
            </div>
            {knowledgeChecksOpen ? (
              <>
                <label htmlFor="study-guide-knowledge-check-list">Click a Knowledge Check title to open it</label>
                <select
                  id="study-guide-knowledge-check-list"
                  className="study-guide-slide-title-list"
                  size="6"
                  defaultValue=""
                  onChange={(event) => {
                    const pageNumber = Number.parseInt(event.target.value, 10);
                    if (Number.isInteger(pageNumber)) {
                      setSlideIndex(pageNumber - 1);
                      setSearchTerm("");
                    }
                    event.target.value = "";
                  }}
                >
                  <option value="" disabled>Select a Knowledge Check slide</option>
                  {deck.knowledgeCheckSlides.map((pageNumber) => (
                    <option key={pageNumber} value={pageNumber}>
                      Slide {pageNumber}: {deck.titles[pageNumber - 1]}
                    </option>
                  ))}
                </select>
              </>
            ) : null}
          </div> : null}
        </div>
      ) : null}
      {deck.knowledgeCheckSlides.length ? (
        <div className="knowledge-check-navigation knowledge-check-slide-navigation">
          <span>
            {currentKnowledgeCheckIndex >= 0
              ? `Knowledge Check ${currentKnowledgeCheckIndex + 1} of ${deck.knowledgeCheckSlides.length}`
              : `Knowledge Check navigation · next is slide ${nextKnowledgeCheck || deck.knowledgeCheckSlides[0]}`}
          </span>
          <div>
            <button
              type="button"
              className="action-button action-button-secondary"
              onClick={() => setSlideIndex(previousKnowledgeCheck - 1)}
              disabled={!previousKnowledgeCheck}
            >
              Previous check
            </button>
            <button
              type="button"
              className="action-button"
              onClick={() => setSlideIndex(nextKnowledgeCheck - 1)}
              disabled={!nextKnowledgeCheck}
            >
              Next check
            </button>
          </div>
        </div>
      ) : null}
      <figure className="study-guide-slide-frame">
        <img src={imageSource} alt={`${title || "Study guide"} — slide ${page}`} />
      </figure>
    </section>
  );
}
